import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { 
  Settings, Search, Building, ClipboardCheck, SearchCheck, 
  AlertTriangle, GraduationCap, FileText, Layers, Monitor,
  ArrowLeft, Check, X
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Module icons
const moduleIcons = {
  jsa: ClipboardCheck,
  audits: SearchCheck,
  incidents: AlertTriangle,
  training: GraduationCap,
  sds: FileText
};

// Module display names
const moduleNames = {
  jsa: "JSA / JHA",
  audits: "Audits",
  incidents: "Incidents / Near Miss",
  training: "Training Passport",
  sds: "SDS World"
};

// AI features per module
const moduleAIFeatures = {
  jsa: ["AI JSA generation", "AI hazard identification", "AI control recommendations"],
  audits: ["AI audit suggestions", "AI scoring guidance", "AI corrective actions"],
  incidents: ["AI narrative writing", "AI root cause analysis", "AI classification"],
  training: ["AI training recommendations", "AI learning paths", "AI refresher suggestions"],
  sds: ["AI hazard extraction", "AI PPE identification", "AI SDS summarization"]
};

export default function ModuleAccess() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyModules, setCompanyModules] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch all companies with their module access
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const res = await axiosInstance.get('/modules/all-companies');
        setCompanies(res.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching companies:', error);
        toast({ title: 'Error', description: 'Failed to load companies', variant: 'destructive' });
        setLoading(false);
      }
    };
    fetchCompanies();
  }, [toast]);

  const handleCompanySelect = async (company) => {
    setSelectedCompany(company);
    try {
      const res = await axiosInstance.get(`/modules/company/${company.id}`);
      setCompanyModules(res.data);
    } catch (error) {
      console.error('Error fetching company modules:', error);
      toast({ title: 'Error', description: 'Failed to load company modules', variant: 'destructive' });
    }
  };

  const handleModuleToggle = async (moduleKey, currentValue) => {
    if (!selectedCompany) return;
    
    setSaving(true);
    try {
      const update = { [moduleKey]: !currentValue };
      const res = await axiosInstance.put(`/modules/company/${selectedCompany.id}`, update);
      setCompanyModules(res.data);
      
      // Update the company in the list
      setCompanies(prev => prev.map(c => 
        c.id === selectedCompany.id 
          ? { ...c, modules: res.data.modules, fullPlatform: res.data.fullPlatform, gcProjectMonitor: res.data.gcProjectMonitor }
          : c
      ));
      
      toast({
        title: !currentValue ? 'Module Unlocked' : 'Module Locked',
        description: `${moduleNames[moduleKey] || moduleKey} has been ${!currentValue ? 'enabled' : 'disabled'}`
      });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update module', variant: 'destructive' });
    }
    setSaving(false);
  };

  const handlePlatformToggle = async (key, currentValue) => {
    if (!selectedCompany) return;
    
    setSaving(true);
    try {
      const update = { [key]: !currentValue };
      const res = await axiosInstance.put(`/modules/company/${selectedCompany.id}`, update);
      setCompanyModules(res.data);
      
      // Update the company in the list
      setCompanies(prev => prev.map(c => 
        c.id === selectedCompany.id 
          ? { ...c, modules: res.data.modules, fullPlatform: res.data.fullPlatform, gcProjectMonitor: res.data.gcProjectMonitor }
          : c
      ));
      
      const label = key === 'full_platform' ? 'Full Platform' : 'GC Project Monitor';
      toast({
        title: !currentValue ? 'Enabled' : 'Disabled',
        description: `${label} has been ${!currentValue ? 'enabled' : 'disabled'}`
      });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update setting', variant: 'destructive' });
    }
    setSaving(false);
  };

  const filteredCompanies = companies.filter(c =>
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Count enabled modules for a company
  const countEnabled = (company) => {
    const modules = company.modules || {};
    return Object.values(modules).filter(Boolean).length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/superadmin')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Settings className="h-6 w-6 text-blue-600" />
              Module Access Control
            </h1>
            <p className="text-sm text-gray-500">Lock or unlock modules for each company</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Company List */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Building className="h-5 w-5" />
              Companies ({companies.length})
            </CardTitle>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search companies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </CardHeader>
          <CardContent className="space-y-2 max-h-[600px] overflow-y-auto">
            {filteredCompanies.map((company) => (
              <div
                key={company.id}
                onClick={() => handleCompanySelect(company)}
                className={`p-3 rounded-lg cursor-pointer transition-all ${
                  selectedCompany?.id === company.id
                    ? 'bg-blue-50 border-2 border-blue-500 dark:bg-blue-900/20'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-800 border-2 border-transparent'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{company.name}</p>
                    <p className="text-xs text-gray-500 truncate">{company.email}</p>
                  </div>
                  <div className="flex items-center gap-1 ml-2">
                    <span className={`text-xs font-medium px-2 py-1 rounded ${
                      countEnabled(company) === 5 
                        ? 'bg-green-100 text-green-700' 
                        : countEnabled(company) === 0
                        ? 'bg-red-100 text-red-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {countEnabled(company)}/5
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Module Controls */}
        <Card className="lg:col-span-2">
          {selectedCompany && companyModules ? (
            <>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>{selectedCompany.name}</span>
                  <span className="text-sm font-normal text-gray-500">{selectedCompany.email}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Individual Modules */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">
                    Modules
                  </h3>
                  <div className="space-y-3">
                    {Object.entries(MODULES).map(([key]) => {
                      const Icon = moduleIcons[key] || Settings;
                      const isEnabled = companyModules.modules?.[key] ?? true;
                      
                      return (
                        <div
                          key={key}
                          className={`p-4 rounded-lg border-2 transition-all ${
                            isEnabled 
                              ? 'bg-green-50 border-green-200 dark:bg-green-900/10 dark:border-green-800' 
                              : 'bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-700'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-lg ${isEnabled ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-200 dark:bg-gray-700'}`}>
                                <Icon className={`h-5 w-5 ${isEnabled ? 'text-green-600' : 'text-gray-400'}`} />
                              </div>
                              <div>
                                <p className="font-medium">{moduleNames[key]}</p>
                                <p className="text-xs text-gray-500">
                                  {isEnabled ? (
                                    <span className="flex items-center gap-1 text-green-600">
                                      <Check className="h-3 w-3" /> AI features enabled
                                    </span>
                                  ) : (
                                    <span className="flex items-center gap-1 text-gray-400">
                                      <X className="h-3 w-3" /> Module locked
                                    </span>
                                  )}
                                </p>
                              </div>
                            </div>
                            <Switch
                              checked={isEnabled}
                              onCheckedChange={() => handleModuleToggle(key, isEnabled)}
                              disabled={saving}
                            />
                          </div>
                          {isEnabled && (
                            <div className="mt-3 pt-3 border-t border-green-200 dark:border-green-800">
                              <p className="text-xs text-gray-500 mb-1">Includes AI:</p>
                              <div className="flex flex-wrap gap-1">
                                {moduleAIFeatures[key]?.map((feature, i) => (
                                  <span key={i} className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded dark:bg-green-900/30 dark:text-green-400">
                                    {feature}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Platform Features */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">
                    Platform Features
                  </h3>
                  <div className="space-y-3">
                    {/* Full Platform */}
                    <div className={`p-4 rounded-lg border-2 transition-all ${
                      companyModules.fullPlatform 
                        ? 'bg-purple-50 border-purple-200 dark:bg-purple-900/10' 
                        : 'bg-gray-50 border-gray-200 dark:bg-gray-800'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${companyModules.fullPlatform ? 'bg-purple-100' : 'bg-gray-200'}`}>
                            <Layers className={`h-5 w-5 ${companyModules.fullPlatform ? 'text-purple-600' : 'text-gray-400'}`} />
                          </div>
                          <div>
                            <p className="font-medium">Full Platform</p>
                            <p className="text-xs text-gray-500">
                              {companyModules.fullPlatform 
                                ? 'Cross-module AI enabled' 
                                : 'Cross-module AI disabled'}
                            </p>
                          </div>
                        </div>
                        <Switch
                          checked={companyModules.fullPlatform}
                          onCheckedChange={() => handlePlatformToggle('full_platform', companyModules.fullPlatform)}
                          disabled={saving}
                        />
                      </div>
                      {companyModules.fullPlatform && (
                        <div className="mt-3 pt-3 border-t border-purple-200">
                          <p className="text-xs text-gray-500 mb-1">Cross-module AI:</p>
                          <div className="flex flex-wrap gap-1">
                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">Incident → JSA recommendations</span>
                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">Incident → Training auto-assign</span>
                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">SDS → JSA auto-linking</span>
                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">Trend-based insights</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* GC Project Monitor */}
                    <div className={`p-4 rounded-lg border-2 transition-all ${
                      companyModules.gcProjectMonitor 
                        ? 'bg-orange-50 border-orange-200 dark:bg-orange-900/10' 
                        : 'bg-gray-50 border-gray-200 dark:bg-gray-800'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${companyModules.gcProjectMonitor ? 'bg-orange-100' : 'bg-gray-200'}`}>
                            <Monitor className={`h-5 w-5 ${companyModules.gcProjectMonitor ? 'text-orange-600' : 'text-gray-400'}`} />
                          </div>
                          <div>
                            <p className="font-medium">GC Project Monitor</p>
                            <p className="text-xs text-gray-500">
                              {companyModules.gcProjectMonitor 
                                ? 'Subcontractor monitoring enabled' 
                                : 'Subcontractor monitoring disabled'}
                            </p>
                          </div>
                        </div>
                        <Switch
                          checked={companyModules.gcProjectMonitor}
                          onCheckedChange={() => handlePlatformToggle('gc_project_monitor', companyModules.gcProjectMonitor)}
                          disabled={saving}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 dark:bg-blue-900/10 dark:border-blue-800">
                  <h4 className="font-medium text-blue-800 dark:text-blue-300 mb-2">How it works</h4>
                  <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1">
                    <li>• <strong>Module ON</strong> = All AI features for that module are enabled</li>
                    <li>• <strong>Module OFF</strong> = Module is locked (user sees "Included with [Module]")</li>
                    <li>• <strong>Full Platform</strong> = Cross-module AI features (like Incident→JSA)</li>
                    <li>• <strong>Always available:</strong> Basic help, grammar checks, SDS viewing, incident reporting</li>
                  </ul>
                </div>
              </CardContent>
            </>
          ) : (
            <CardContent className="flex items-center justify-center min-h-[500px]">
              <div className="text-center text-gray-500">
                <Building className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium">Select a company</p>
                <p className="text-sm">Choose a company to manage their module access</p>
              </div>
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
}

// Module definitions constant for the component
const MODULES = {
  jsa: { name: "JSA / JHA Module" },
  audits: { name: "Audits Module" },
  incidents: { name: "Incidents / Near Miss Module" },
  training: { name: "Training Passport Module" },
  sds: { name: "SDS World Module" }
};
